import { useState } from 'react';
import { findExchange } from '../utils/exchanges';
import { useTheme } from '../context/ThemeContext';

// Einheitliches Logo-Tile für eine Börse — Brand-Color-Kreis mit SVG-Logo darin.
// Bei fehlendem Logo oder Lade-Fehler: Initiale in Brand-Color (Graceful Fallback).
//
// Props:
//   name       — Exchange-Name (z.B. "Binance") oder ID; case-insensitive
//   size       — px Durchmesser des Tiles (default 32)
//   showLabel  — Label rechts neben/unter dem Tile rendern
//   labelBelow — Label unterhalb statt rechts daneben
//   bare       — keine Hintergrund-/Border-Hülle, nur das nackte Logo (für kleine Inline-Icons)
// Initiale für den Logo-Fallback: bei echtem Custom-Label dessen ersten
// Buchstaben, sonst die Initiale des aufgelösten Eintrags ('A' für Andere).
function fallbackInitial(name, ex) {
  const raw = String(name || '').trim();
  const isGenericOther = !raw || raw.toLowerCase() === 'andere' || raw.toLowerCase() === 'other';
  if (ex.id === 'other' && !isGenericOther) return raw[0].toUpperCase();
  return ex.label[0];
}

export default function ExchangeLogo({
  name,
  size = 32,
  showLabel = false,
  labelBelow = false,
  style,
  labelStyle,
}) {
  const ex = findExchange(name);
  const { isDark } = useTheme();
  const [broken, setBroken] = useState(false);
  const hasLogo = ex.logo && !broken;
  // Schwarze/sehr dunkle Logos (Ledger, Trezor, OKX, KeepKey, Exodus) sind auf
  // dem dunklen App-Hintergrund unsichtbar — per CSS-Filter invertieren.
  const invertFilter = isDark && ex.invertOnDark ? 'invert(1)' : undefined;

  // Tile-Style: kein Wrapper-Ring, kein Glow — der SVG bringt seine eigene
  // brand-gefärbte rounded-rect-Hintergrund mit, das IST der Tile.
  const tileStyle = {
    width: size, height: size,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  };

  const tile = (
    <span style={tileStyle}>
      {hasLogo ? (
        <img
          src={ex.logo}
          alt={ex.label}
          width={size}
          height={size}
          onError={() => setBroken(true)}
          style={{ display: 'block', borderRadius: Math.round(size * 0.22), filter: invertFilter }}
        />
      ) : (
        // Fallback nur wenn SVG fehlt: schlichter brand-farbener Block mit Initiale.
        // Bei unbekanntem Custom-Label (findExchange → 'other') die Initiale des
        // ÜBERGEBENEN Namens nehmen, nicht stumpf 'A' (Andere) — sonst sehen alle
        // Custom-Wallets (Backpack, Solflare, Rabby …) gleich aus.
        <span style={{
          width: size, height: size,
          borderRadius: Math.round(size * 0.22),
          background: ex.color,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: '#FFFFFF', fontWeight: 800,
          fontSize: Math.round(size * 0.48),
          letterSpacing: '-0.04em',
          fontFamily: "'Manrope', sans-serif",
        }}>{fallbackInitial(name, ex)}</span>
      )}
    </span>
  );

  if (!showLabel) {
    return <span style={{ display: 'inline-flex', ...style }}>{tile}</span>;
  }

  if (labelBelow) {
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 6, ...style }}>
        {tile}
        <span style={{ fontSize: 10.5, color: 'inherit', ...labelStyle }}>{ex.label}</span>
      </span>
    );
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...style }}>
      {tile}
      <span style={{ fontSize: 13, fontWeight: 600, ...labelStyle }}>{ex.label}</span>
    </span>
  );
}
